"use client";

import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import Button from "@/components/ui/button/Button";
import { useSuburbs, useCreateSuburb, useDeleteSuburb } from "@/hooks/data/useLocations";
import { toast } from "@/ui/Toast";
import { userMessage } from "@/api/errors";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  locationId: string | null;
  locationName?: string;
}

/** Suburbs are pure display/filter granularity under a city (Location) — no pricing fields of
 * their own (see Suburb's backend doc comment). Real per-tenant example: Auckland ships with
 * CBD/Ponsonby/North Shore/etc. seeded; any city can have its own list added here. */
export default function SuburbsModal({ isOpen, onClose, locationId, locationName }: Props) {
  const { data: suburbs = [], isLoading } = useSuburbs(locationId);
  const create = useCreateSuburb(locationId ?? "");
  const remove = useDeleteSuburb(locationId ?? "");
  const [name, setName] = useState("");

  const add = async () => {
    if (!name.trim()) return;
    try {
      await create.mutateAsync({ name: name.trim(), sortOrder: suburbs.length });
      setName("");
    } catch (err) {
      toast.error(userMessage(err));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white p-6 dark:bg-gray-900">
        <h4 className="mb-1 text-xl font-semibold text-gray-800 dark:text-white/90">Suburbs</h4>
        {locationName && <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">{locationName}</p>}

        <div className="mb-4 flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="e.g. Ponsonby"
            className="h-9 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <Button size="sm" disabled={create.isPending || !name.trim()} onClick={add}>Add</Button>
        </div>

        <div className="max-h-[40vh] space-y-1 overflow-y-auto pr-1">
          {isLoading ? (
            <p className="py-4 text-center text-sm text-gray-500">Loading…</p>
          ) : suburbs.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">No suburbs listed yet.</p>
          ) : (
            suburbs.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700">
                {s.name}
                <button
                  onClick={async () => {
                    try {
                      await remove.mutateAsync(s.id);
                    } catch (err) {
                      toast.error(userMessage(err));
                    }
                  }}
                  className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
